import React from 'react'
import download from 'downloadjs'
import { combineEpics, ofType } from 'redux-observable'
import { of } from 'rxjs'
import { catchError, filter, map, mergeMap, takeUntil } from 'rxjs/operators'

import { getRangeFromDateFilter } from 'utils/dateUtils'
import { mapToLabels, wrapCSVTextToBlob, xhrExtractFileName } from 'utils/helpers'
import { URL_CONST } from 'utils/urlConsts'
import { matchURL } from 'utils/urls'

import { FETCH_SELECTED_COMPANY_DATA_ASYNC } from 'containers/PartnerOptions/constants'
import { selectCompanyFilter } from 'containers/PartnerOptions/selectors'
import { addToast } from 'containers/Toasts/actions'

import { LIST_TYPE } from '../constants'

import {
  createIncidentError,
  createIncidentSuccess,
  fetchDataError,
  fetchDataSuccess,
  fetchIncidentsCSVError,
  fetchIncidentsCSVSuccess,
} from './actions'
import {
  CREATE_INCIDENT_ASYNC,
  DATA_REQUEST_ASYNC,
  DATE_FILTER_CHANGE,
  FETCH_INCIDENTS_CSV_ASYNC,
  FILTER_CHANGE,
  PAGINATE_CHANGE,
  SEARCH_CHANGE,
} from './constants'
import {
  selectBannerTypeFilter,
  selectDateRange,
  selectDepartmentFilter,
  selectIncidentTypeFilter,
  selectListType,
  selectPage,
  selectReportedByFilter,
  selectResolutionTypeFilter,
  selectResolvedByFilter,
  selectSearch,
  selectTagsFilter,
  selectThreatTypeFilter,
} from './selectors'

const fetchDataEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(
      FILTER_CHANGE,
      SEARCH_CHANGE,
      DATE_FILTER_CHANGE,
      FETCH_SELECTED_COMPANY_DATA_ASYNC.SUCCESS,
      DATA_REQUEST_ASYNC.INITIATE,
      CREATE_INCIDENT_ASYNC.SUCCESS,
      PAGINATE_CHANGE
    ),
    filter((action) => {
      if (action.type === FETCH_SELECTED_COMPANY_DATA_ASYNC.SUCCESS)
        return matchURL(URL_CONST.dashboard.mitigation)
      return true
    }),
    mergeMap(() => {
      const state = state$.value
      const isBanners = selectListType(state) === LIST_TYPE.BANNERS
      const selectedCompany = selectCompanyFilter(state)
      const page = selectPage(state)
      const dateFilter = selectDateRange(state)
      const search = selectSearch(state)
      const bannerTypeFilter = selectBannerTypeFilter(state)
      const incidentTypeFilter = selectIncidentTypeFilter(state)
      const tagsFilter = selectTagsFilter(state)
      const departmentFilter = selectDepartmentFilter(state)
      const resolvedByFilter = selectResolvedByFilter(state)
      const reportedByFilter = selectReportedByFilter(state)
      const resolutionTypeFilter = selectResolutionTypeFilter(state)
      const threatTypeFilter = selectThreatTypeFilter(state)

      return (
        isBanners
          ? api.dashboard.getThreatProtectionBannersList(
              selectedCompany?.value,
              page || 1,
              dateFilter,
              search,
              bannerTypeFilter.map((elem) => elem.value)
            )
          : api.dashboard.getThreatProtectionIncidentsList(
              selectedCompany?.value,
              false,
              false,
              page || 1,
              getRangeFromDateFilter(dateFilter),
              search,
              mapToLabels(incidentTypeFilter),
              mapToLabels(tagsFilter),
              mapToLabels(departmentFilter),
              mapToLabels(resolvedByFilter),
              mapToLabels(reportedByFilter),
              mapToLabels(resolutionTypeFilter),
              mapToLabels(threatTypeFilter)
            )
      ).pipe(
        map((response) => fetchDataSuccess(response.response)),
        catchError((error) => of(epicsErrorHandler(fetchDataError, error))),
        takeUntil(action$.pipe(ofType(DATA_REQUEST_ASYNC.CANCEL)))
      )
    })
  )

const fetchIncidentsCSVEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(FETCH_INCIDENTS_CSV_ASYNC.INITIATE),
    mergeMap(() => {
      const state = state$.value
      const selectedCompany = selectCompanyFilter(state)
      const page = selectPage(state)
      const dateFilter = selectDateRange(state)
      const search = selectSearch(state)
      const incidentTypeFilter = selectIncidentTypeFilter(state)
      const tagsFilter = selectTagsFilter(state)
      const departmentFilter = selectDepartmentFilter(state)
      const resolvedByFilter = selectResolvedByFilter(state)
      const reportedByFilter = selectReportedByFilter(state)
      const resolutionTypeFilter = selectResolutionTypeFilter(state)
      const threatTypeFilter = selectThreatTypeFilter(state)

      return api.dashboard
        .getThreatProtectionIncidentsList(
          selectedCompany?.value,
          true,
          false,
          page || 1,
          getRangeFromDateFilter(dateFilter),
          search,
          mapToLabels(incidentTypeFilter),
          mapToLabels(tagsFilter),
          mapToLabels(departmentFilter),
          mapToLabels(resolvedByFilter),
          mapToLabels(reportedByFilter),
          mapToLabels(resolutionTypeFilter),
          mapToLabels(threatTypeFilter)
        )
        .pipe(
          map((response) => {
            download(
              wrapCSVTextToBlob(response.response),
              xhrExtractFileName(response.xhr),
              response.responseType
            )
            return fetchIncidentsCSVSuccess(response)
          }),
          catchError((error) => of(epicsErrorHandler(fetchIncidentsCSVError, error))),
          takeUntil(action$.pipe(ofType(FETCH_INCIDENTS_CSV_ASYNC.CANCEL)))
        )
    })
  )

const createIncidentEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(CREATE_INCIDENT_ASYNC.INITIATE),
    mergeMap((action) =>
      api.dashboard
        .createIncidentFromMitigationList(
          state$.value.partnerOptions.selectedCompany?.value,
          action.payload.eid
        )
        .pipe(
          mergeMap((response) =>
            of(
              createIncidentSuccess(response),
              addToast({
                message: (
                  <span>
                    Incident
                    <a
                      className="white cp b"
                      href={`${URL_CONST.incidents.attackdetails}/${response?.response.incidentId}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {` IS-${response?.response.incidentId} `}
                    </a>
                    has been successfully created
                  </span>
                ),
              })
            )
          ),
          catchError((error) =>
            of(
              epicsErrorHandler(createIncidentError, error),
              addToast({ message: error.response?.message, type: 'error' })
            )
          ),
          takeUntil(action$.pipe(ofType(CREATE_INCIDENT_ASYNC.CANCEL)))
        )
    )
  )

export default combineEpics(fetchDataEpic, fetchIncidentsCSVEpic, createIncidentEpic)
